import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import SinglePage from "../SinglePage/SinglePage";
import { clearAuthSession, AUTH_CHANGED_EVENT, loadAuthSession } from "../../../services/authStorage";
import {
  ActionsRow,
  Avatar,
  Badge,
  Card,
  ContentGrid,
  Eyebrow,
  FormActions,
  FormGrid,
  HeroBanner,
  HeroText,
  HeroTitle,
  HintText,
  InlineMessage,
  Label,
  ProfileHeader,
  SectionStack,
  StatCard,
  StatGrid,
  TwoColumnGrid,
  Value,
} from "./styles";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../../values/colors";
import { changeOwnPassword, UserStatus } from "../../../services/userApi";
import { formatGermanDateTime } from "../../../utils/dateTime";

const ROLE_LABELS: Record<"mitglied" | "shopmanager" | "administrator", string> = {
  mitglied: "Mitglied",
  shopmanager: "Shopmanager",
  administrator: "Administrator",
};

const STATUS_LABELS: Record<UserStatus, string> = {
  aktiv: "Aktiv",
  passiv: "Passiv",
  inaktiv: "Inaktiv",
};

const PASSWORD_FIELD_SX = {
  color: COLORS.white,
  borderRadius: "1rem",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.15)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(181,115,76,0.4)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.apronBrown,
  },
};

const PASSWORD_LABEL_SX = {
  color: "rgba(255,255,255,0.55)",
};

const Account = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(loadAuthSession());
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  useEffect(() => {
    const syncSession = () => setSession(loadAuthSession());
    const syncSessionFromStorage = (event: StorageEvent) => {
      if (event.key && !event.key.startsWith("eckenmoorhexen.auth.")) {
        return;
      }

      syncSession();
    };

    window.addEventListener(AUTH_CHANGED_EVENT, syncSession);
    window.addEventListener("storage", syncSessionFromStorage);

    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncSession);
      window.removeEventListener("storage", syncSessionFromStorage);
    };
  }, []);

  const initials = useMemo(() => {
    if (!session?.user) {
      return "EH";
    }

    return `${session.user.vorname.charAt(0)}${session.user.name.charAt(0)}`.toUpperCase();
  }, [session]);

  const handleLogout = () => {
    clearAuthSession();
    navigate("/");
  };

  const openShop = () => {
    window.open("/shop", "_blank", "noopener,noreferrer");
  };

  const openAdmin = () => {
    navigate("/admin/mitglieder");
  };

  const openShopReservationsManagement = () => {
    navigate("/shopverwaltung/reservierungen");
  };

  const openOrders = () => {
    navigate("/konto/reservierungen");
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordMessage(null);

    if (!session?.token) {
      setPasswordMessage({ tone: "error", text: "Du musst angemeldet sein, um dein Passwort zu ändern." });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ tone: "error", text: "Die neuen Passwörter stimmen nicht überein." });
      return;
    }

    setIsPasswordSubmitting(true);

    try {
      await changeOwnPassword(session.token, {
        aktuellesPasswort: currentPassword,
        neuesPasswort: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage({
        tone: "success",
        text: "Dein Passwort wurde aktualisiert. Deine aktuelle Sitzung bleibt bestehen.",
      });
    } catch (error) {
      setPasswordMessage({
        tone: "error",
        text: error instanceof Error ? error.message : "Passwort konnte nicht aktualisiert werden.",
      });
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const isAdministrator = session?.user?.role === "administrator";
  const isShopManager = session?.user?.role === "shopmanager";

  return (
    <SinglePage
      title="Mein Konto"
      heroBanner={
        <HeroBanner>
          <Eyebrow>Benutzerkonto</Eyebrow>
          <HeroTitle>Dein Zugang zu den Eckenmoorhexen.</HeroTitle>
          <HeroText>
            Hier siehst du deine hinterlegten Kontodaten, deinen aktuellen Login-Status
            und schnelle Wege in den Shop oder zurück auf die Startseite.
          </HeroText>
        </HeroBanner>
      }
      content={
        <ContentGrid>
          <TwoColumnGrid>
            <Card>
              <ProfileHeader>
                <Avatar>{initials}</Avatar>
                <div>
                  <Label>Aktueller Status</Label>
                  <Value>
                    {session?.user
                      ? `${session.user.vorname} ${session.user.name}`
                      : "Nicht angemeldet"}
                  </Value>
                </div>
              </ProfileHeader>

              {session?.user ? (
                <>
                  <Badge>Session aktiv bis {formatGermanDateTime(session.expiresAtUtc)}</Badge>
                  <StatGrid style={{ marginTop: "1.5rem" }}>
                    <StatCard>
                      <Label>E-Mail</Label>
                      <Value>{session.user.email}</Value>
                    </StatCard>
                    <StatCard>
                      <Label>Kleidlesnummer</Label>
                      <Value>{session.user.kleidlesnummer}</Value>
                    </StatCard>
                    <StatCard>
                      <Label>Rolle</Label>
                      <Value>{ROLE_LABELS[session.user.role] ?? session.user.role}</Value>
                    </StatCard>
                    <StatCard>
                      <Label>Status</Label>
                      <Value>{STATUS_LABELS[session.user.status] ?? session.user.status}</Value>
                    </StatCard>
                    <StatCard>
                      <Label>Vorname</Label>
                      <Value>{session.user.vorname}</Value>
                    </StatCard>
                    <StatCard>
                      <Label>Name</Label>
                      <Value>{session.user.name}</Value>
                    </StatCard>
                  </StatGrid>
                </>
              ) : (
                <HintText>
                  Du bist aktuell nicht angemeldet. Öffne das Benutzer-Menü in der Navbar,
                  um dich anzumelden. Danach erscheinen hier deine Kontodaten.
                </HintText>
              )}
            </Card>

            {session?.user && (
              <Card>
                <Label>Schnellzugriffe</Label>
                <Value style={{ marginBottom: "0.75rem" }}>Direkt weiter</Value>
                <HintText>
                  Wechsel in den Shop, rufe deine Reservierungen auf oder nutze zusätzliche Bereiche,
                  die für deine Rolle freigeschaltet sind.
                </HintText>
                <ActionsRow>
                  <Button
                    variant="contained"
                    onClick={openShop}
                    sx={{
                      borderRadius: "999px",
                      paddingInline: "1.4rem",
                      paddingBlock: "0.62rem",
                      textTransform: "none",
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
                      boxShadow: "0 12px 26px rgba(0, 0, 0, 0.28)",
                    }}
                  >
                    Zum Shop
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={openOrders}
                    sx={{
                      borderRadius: "999px",
                      paddingInline: "1.4rem",
                      paddingBlock: "0.62rem",
                      textTransform: "none",
                      fontWeight: 700,
                      color: COLORS.white,
                      borderColor: "rgba(181,115,76,0.24)",
                      background: "rgba(181,115,76,0.08)",
                    }}
                  >
                    Meine Reservierungen
                  </Button>
                  {isShopManager && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={openShopReservationsManagement}
                      sx={{
                        borderRadius: "999px",
                        paddingInline: "1.4rem",
                        paddingBlock: "0.62rem",
                        textTransform: "none",
                        fontWeight: 700,
                        color: COLORS.white,
                        borderColor: "rgba(181,115,76,0.24)",
                        background: "rgba(181,115,76,0.08)",
                      }}
                    >
                      Reservierungen verwalten
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleLogout}
                    sx={{
                      borderRadius: "999px",
                      paddingInline: "1.4rem",
                      paddingBlock: "0.62rem",
                      textTransform: "none",
                      fontWeight: 700,
                      color: COLORS.white,
                      borderColor: "rgba(255,255,255,0.18)",
                      background: "rgba(255,255,255,0.03)",
                    }}
                  >
                    Abmelden
                  </Button>
                  {isAdministrator && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={openAdmin}
                      sx={{
                        borderRadius: "999px",
                        paddingInline: "1.4rem",
                        paddingBlock: "0.62rem",
                        textTransform: "none",
                        fontWeight: 700,
                        color: COLORS.white,
                        borderColor: "rgba(155,207,103,0.28)",
                        background: "rgba(155,207,103,0.08)",
                      }}
                    >
                      Mitgliederverwaltung
                    </Button>
                  )}
                </ActionsRow>
              </Card>
            )}
          </TwoColumnGrid>

          <TwoColumnGrid>
            {session?.user && (
              <Card>
                <Label>Sicherheit</Label>
                <Value style={{ marginBottom: "0.75rem" }}>Passwort ändern</Value>
                <SectionStack as="form" onSubmit={handlePasswordSubmit}>
                  <HintText>
                    Vergib hier selbst ein neues Passwort für dein Konto. Es muss mindestens 12 Zeichen lang sein
                    sowie Groß-, Kleinbuchstaben und Zahlen enthalten.
                  </HintText>
                  <FormGrid>
                    <TextField
                      label="Aktuelles Passwort"
                      type="password"
                      value={currentPassword}
                      onChange={(event) => setCurrentPassword(event.target.value)}
                      required
                      fullWidth
                      sx={{ gridColumn: "1 / -1" }}
                      InputLabelProps={{ sx: PASSWORD_LABEL_SX }}
                      InputProps={{ sx: PASSWORD_FIELD_SX }}
                    />
                    <TextField
                      label="Neues Passwort"
                      type="password"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      required
                      fullWidth
                      InputLabelProps={{ sx: PASSWORD_LABEL_SX }}
                      InputProps={{ sx: PASSWORD_FIELD_SX }}
                    />
                    <TextField
                      label="Neues Passwort wiederholen"
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      required
                      fullWidth
                      InputLabelProps={{ sx: PASSWORD_LABEL_SX }}
                      InputProps={{ sx: PASSWORD_FIELD_SX }}
                    />
                  </FormGrid>
                  {passwordMessage ? <InlineMessage $tone={passwordMessage.tone}>{passwordMessage.text}</InlineMessage> : null}
                  <FormActions>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isPasswordSubmitting}
                      sx={{
                        borderRadius: "999px",
                        paddingInline: "1.4rem",
                        paddingBlock: "0.62rem",
                        textTransform: "none",
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
                        boxShadow: "0 12px 26px rgba(0, 0, 0, 0.28)",
                      }}
                    >
                      {isPasswordSubmitting ? "Speichere..." : "Passwort aktualisieren"}
                    </Button>
                  </FormActions>
                </SectionStack>
              </Card>
            )}

            <Card>
              <Label>Hinweis</Label>
              <Value style={{ marginBottom: "0.75rem" }}>Kein offener Signup</Value>
              <HintText>
                Neue Accounts werden nicht über die Oberfläche erstellt. Die Anmeldung
                funktioniert nur mit bestehenden Zugangsdaten.
              </HintText>
            </Card>
          </TwoColumnGrid>
        </ContentGrid>
      }
    />
  );
};

export default Account;
